import {useCallback, useEffect, useRef, useState} from 'react'

/**
 * Open/close state for the marketing-header mobile menu, replacing the legacy
 * `details:toggled` flow with React state. The consumer derives the header's
 * `open` / `Details--on` classes from `isOpen`.
 */
export function useHeaderMenu() {
  const [isOpen, setIsOpen] = useState(false)
  const toggleRef = useRef<HTMLButtonElement | null>(null)

  const open = useCallback(() => setIsOpen(true), [])

  const close = useCallback((options?: {restoreFocus?: boolean}) => {
    setIsOpen(false)
    if (options?.restoreFocus) toggleRef.current?.focus()
  }, [])

  const toggle = useCallback(() => setIsOpen(previous => !previous), [])

  // Escape closes the menu and restores focus to the toggle; only bound while open.
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Escape') {
        setIsOpen(false)
        toggleRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  return {isOpen, open, close, toggle, toggleRef}
}
